import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Lock, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useSubscription } from "@/hooks/useSubscription";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";

interface Props {
  children: ReactNode;
}

/**
 * Renders a full-page paywall when the authenticated real-mode user has no
 * active subscription. Demo mode and loading states pass through.
 * Settings is always reachable so the user can re-subscribe / open the portal.
 */
export function DashboardPaywallGate({ children }: Props) {
  const { mode, isAuthenticated } = useAuth();
  const { hasAccess, loading, subscription } = useSubscription();

  if (!isAuthenticated || mode !== "real" || loading || hasAccess) {
    return <>{children}</>;
  }

  const wasCanceled = subscription?.status === "canceled";

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="border-b border-border/50 px-4 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/"><Logo variant="full" className="h-12 sm:h-14 object-contain" /></Link>
          <Link to="/settings" className="text-sm text-muted-foreground hover:text-foreground">
            Account
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-xl border-primary/30">
          <CardContent className="p-8 text-center space-y-6">
            <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="w-7 h-7 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">
                {wasCanceled ? "Your subscription has ended" : "Your dashboard is locked"}
              </h1>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                {wasCanceled
                  ? "Resubscribe to restore full access to deals, activities, leaderboard, and CSI. Your data is preserved."
                  : "Start your 14-day free trial to unlock the full Pull Over Sales dashboard. No charge until day 15. Cancel anytime."}
              </p>
            </div>

            <div className="text-left space-y-2 max-w-sm mx-auto">
              {[
                "Real-time deal pipeline & stall alerts",
                "Rep leaderboard and CSI tracking",
                "Push notifications for hot deals",
                "Manager dashboard & invite reps",
              ].map((line) => (
                <div key={line} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                  <span>{line}</span>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Link to="/pricing">
                <Button size="lg" className="w-full sm:w-auto">
                  <Sparkles className="h-4 w-4 mr-2" />
                  {wasCanceled ? "Resubscribe" : "See plans & start trial"}
                </Button>
              </Link>
              <Link to="/settings">
                <Button size="lg" variant="outline" className="w-full sm:w-auto">
                  Manage account
                </Button>
              </Link>
            </div>

            <p className="text-xs text-muted-foreground">
              Payments handled by Paddle.com (Merchant of Record).
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
